import {
    AddOutlined,
    CloseOutlined,
    DescriptionOutlined,
    DownloadOutlined,
    FullscreenOutlined,
    HideImageOutlined,
    SaveOutlined,
} from "@mui/icons-material";
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    Checkbox,
    Chip,
    CircularProgress,
    Fab,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormLabel,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Stack,
    Switch,
    SwitchProps,
    TextField,
    Tooltip,
    Typography,
    styled,
} from "@mui/material";
import {
    ChangeEvent,
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useCookies } from "react-cookie";
import findDriverParameterByDriverAndCustomer from "../../../../resources/Api/driverparameter/findDriverParameterByDriverAndCustomer";
import saveDriverParameter from "../../../../resources/Api/driverparameter/saveDriverParameter";
import workScheduleFindAllListResponse from "../../../../resources/Api/workschedule/workScheduleFindAllListResponse";
import {
    DriverAttachment,
    DriverDialogSection,
    DriverParameter as DriverParameterModel,
    WorkScheduleListType,
} from "../../../../types/types";
import { getApiErrorMessage } from "../../../../functions/functions";
import { useCustomerFeatures } from "../../../../hooks/useCustomerFeatures";
import {
    WEEK_DAYS,
    buildDriverParameterPayload,
    createInitialDriverParameter,
    normalizeDriverParameterResponse,
} from "./driverParameterModel";

type StatusMessage = "warn" | "success" | "info" | "error" | undefined;

type DriverParameterProps = {
    driverId: number;
    customerId: number;
    section: DriverDialogSection;
    isDisabled: boolean;
    setTextValidation: Dispatch<SetStateAction<string>>;
    setStatusMessage: Dispatch<SetStateAction<StatusMessage>>;
    setFieldsIsValid: Dispatch<SetStateAction<boolean>>;
    onUnsavedChangesChange: (hasUnsavedChanges: boolean) => void;
    onClose: () => void;
};

type AttachmentCardProps = {
    attachment: DriverAttachment;
    editableDescription?: boolean;
    onDescriptionChange?: (description: string) => void;
    onDownload: () => void;
    onFullscreen: () => void;
    onRemove?: () => void;
};

const MAX_ATTACHMENTS = 3;

const GreenSwitch = styled((props: SwitchProps) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    "& .MuiSwitch-switchBase": {
        padding: 0,
        margin: 2,
        transitionDuration: "300ms",
        "&.Mui-checked": {
            transform: "translateX(16px)",
            color: "#fff",
            "& + .MuiSwitch-track": {
                backgroundColor: "#65C466",
                opacity: 1,
                border: 0,
            },
        },
        "&.Mui-focusVisible .MuiSwitch-thumb": {
            color: "#33cf4d",
            border: "6px solid #fff",
        },
        "&.Mui-disabled .MuiSwitch-thumb": {
            color: theme.palette.grey[100],
        },
        "&.Mui-disabled + .MuiSwitch-track": {
            opacity: 0.5,
        },
    },
    "& .MuiSwitch-thumb": {
        boxSizing: "border-box",
        width: 22,
        height: 22,
    },
    "& .MuiSwitch-track": {
        borderRadius: 13,
        backgroundColor: "#E9E9EA",
        opacity: 1,
        transition: theme.transitions.create(["background-color"], {
            duration: 500,
        }),
    },
}));

const AttachmentCard = ({
    attachment,
    editableDescription = false,
    onDescriptionChange,
    onDownload,
    onFullscreen,
    onRemove,
}: AttachmentCardProps) => (
    <Card
        className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-black/10 tw-bg-white tw-shadow-sm tw-transition-all tw-duration-200 hover:tw-translate-y-[-1px] hover:tw-shadow-md"
        sx={{
            width: 190,
            background: "#ffff",
            padding: 0.5,
            margin: 0.5,
            border: "1px solid rgba(0, 63, 74, 0.10)",
            borderRadius: 3,
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.06)",
            transition: "transform 200ms ease, box-shadow 200ms ease",
        }}
    >
        {attachment.file ? (
            <Box
                component="img"
                src={attachment.file}
                alt={attachment.name || attachment.description || "Anexo do condutor"}
                sx={{
                    objectFit: "cover",
                    width: 170,
                    height: 150,
                    display: "block",
                    borderRadius: 2,
                    background: "gray",
                    margin: "5px auto",
                }}
            />
        ) : (
            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ width: 170, height: 150, margin: "5px auto" }}
            >
                <HideImageOutlined sx={{ fontSize: 44, color: "#6c757d" }} />
                <Typography variant="caption">Imagem indisponível</Typography>
            </Stack>
        )}

        {editableDescription ? (
            <TextField
                fullWidth
                size="small"
                label="Descrição do anexo"
                value={attachment.description ?? ""}
                placeholder="Digite uma descrição (Opcional)"
                InputLabelProps={{ shrink: true }}
                inputProps={{ maxLength: 250 }}
                onChange={(event) => onDescriptionChange?.(event.target.value)}
                sx={{ marginTop: 1 }}
            />
        ) : (
            <Typography
                variant="caption"
                title={attachment.description || attachment.name}
                sx={{
                    display: "block",
                    fontFamily: "Prometo",
                    textAlign: "center",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    padding: "4px 8px 0",
                }}
            >
                {attachment.description || attachment.name || `Anexo #${attachment.id}`}
            </Typography>
        )}

        <CardActions sx={{ justifyContent: "center" }}>
            <Tooltip title="Baixar anexo">
                <span>
                    <IconButton
                        size="small"
                        onClick={onDownload}
                        disabled={!attachment.file}
                        aria-label="Baixar anexo do condutor"
                    >
                        <DownloadOutlined sx={{ color: "#00B700" }} />
                    </IconButton>
                </span>
            </Tooltip>

            {onRemove && (
                <Tooltip title="Remover anexo">
                    <IconButton
                        size="small"
                        onClick={onRemove}
                        aria-label="Remover anexo do condutor"
                    >
                        <HideImageOutlined sx={{ color: "red" }} />
                    </IconButton>
                </Tooltip>
            )}

            <Tooltip title="Visualizar em tela cheia">
                <span>
                    <IconButton
                        size="small"
                        onClick={onFullscreen}
                        disabled={!attachment.file}
                        aria-label="Visualizar anexo do condutor em tela cheia"
                    >
                        <FullscreenOutlined sx={{ color: "#1976d2" }} />
                    </IconButton>
                </span>
            </Tooltip>
        </CardActions>
    </Card>
);

const DriverParameter = ({
    driverId,
    customerId,
    section,
    isDisabled,
    setTextValidation,
    setStatusMessage,
    setFieldsIsValid,
    onUnsavedChangesChange,
    onClose,
}: DriverParameterProps) => {
    const [cookies] = useCookies(["token"]);
    const customerFeatures = useCustomerFeatures(customerId);
    const workSchedulesEnabled = customerFeatures?.useWorkSchedules === true;
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [driverParameter, setDriverParameter] = useState<DriverParameterModel>(
        createInitialDriverParameter(driverId, customerId),
    );
    const [initialDriverParameter, setInitialDriverParameter] =
        useState<DriverParameterModel | null>(null);
    const [newFiles, setNewFiles] = useState<DriverAttachment[]>([]);
    const [removedFiles, setRemovedFiles] = useState<number[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [workSchedulesList, setWorkSchedulesList] = useState<WorkScheduleListType[]>([]);

    const showMessage = useCallback(
        (message: string, status: StatusMessage) => {
            setTextValidation(message);
            setStatusMessage(status);
            setFieldsIsValid(false);
        },
        [setFieldsIsValid, setStatusMessage, setTextValidation],
    );

    const resetAttachmentChanges = useCallback(() => {
        setNewFiles([]);
        setRemovedFiles([]);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    }, []);

    const loadDriverParameter = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const emptyParameter = createInitialDriverParameter(driverId, customerId);

        if (driverId <= 0 || customerId <= 0) {
            setDriverParameter(emptyParameter);
            setInitialDriverParameter(emptyParameter);
            resetAttachmentChanges();
            return;
        }

        setLoading(true);

        try {
            const response = await findDriverParameterByDriverAndCustomer(
                token,
                driverId,
                customerId,
            );
            const loadedParameter = normalizeDriverParameterResponse(
                response.data,
                driverId,
                customerId,
            );

            setDriverParameter(loadedParameter);
            setInitialDriverParameter(loadedParameter);
            resetAttachmentChanges();
            onUnsavedChangesChange(false);
        } catch (error: any) {
            if (error?.response?.status === 404) {
                setDriverParameter(emptyParameter);
                setInitialDriverParameter(emptyParameter);
                resetAttachmentChanges();
                onUnsavedChangesChange(false);
            } else {
                console.error("Erro ao buscar parâmetros do condutor:", error);
                setDriverParameter(emptyParameter);
                setInitialDriverParameter(emptyParameter);
                resetAttachmentChanges();
                showMessage(
                    getApiErrorMessage(
                        error,
                        "Não foi possível carregar os parâmetros do condutor.",
                    ),
                    "error",
                );
            }
        } finally {
            setLoading(false);
        }
    }, [
        cookies.token,
        customerId,
        driverId,
        onUnsavedChangesChange,
        resetAttachmentChanges,
        showMessage,
    ]);

    useEffect(() => {
        void loadDriverParameter();
    }, [loadDriverParameter]);

    const loadWorkSchedules = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");

        if (customerId <= 0 || !workSchedulesEnabled) {
            setWorkSchedulesList([]);
            return;
        }

        try {
            const response = await workScheduleFindAllListResponse(token, customerId);
            setWorkSchedulesList(response.data);
        } catch (error) {
            console.error("Erro ao buscar escalas de trabalho:", error);
            setWorkSchedulesList([]);
        }
    }, [cookies.token, customerId, workSchedulesEnabled]);

    useEffect(() => {
        void loadWorkSchedules();
    }, [loadWorkSchedules]);

    const hasChanges = useCallback(() => {
        if (!initialDriverParameter) {
            return false;
        }

        const normalizeNumber = (value?: number | null) =>
            value == null || Number.isNaN(Number(value)) ? null : Number(value);
        const normalizeString = (value?: string | null) => value?.trim() ?? "";
        const normalizeDays = (days?: number[] | null) =>
            [...(days ?? [])].sort((a, b) => a - b).join(",");

        return (
            normalizeNumber(driverParameter.workScheduleId) !==
            normalizeNumber(initialDriverParameter.workScheduleId) ||
            normalizeDays(driverParameter.daysOff) !==
            normalizeDays(initialDriverParameter.daysOff) ||
            driverParameter.makesExternalTrips !== initialDriverParameter.makesExternalTrips ||
            driverParameter.usesDriverLogin !== initialDriverParameter.usesDriverLogin ||
            driverParameter.receivesAlerts !== initialDriverParameter.receivesAlerts ||
            normalizeString(driverParameter.observation) !==
            normalizeString(initialDriverParameter.observation) ||
            newFiles.length > 0 ||
            removedFiles.length > 0
        );
    }, [driverParameter, initialDriverParameter, newFiles.length, removedFiles.length]);

    useEffect(() => {
        onUnsavedChangesChange(hasChanges());
    }, [hasChanges, onUnsavedChangesChange]);

    useEffect(
        () => () => onUnsavedChangesChange(false),
        [onUnsavedChangesChange],
    );

    const validate = useCallback((): string | null => {
        if (driverId <= 0) {
            return "Condutor inválido, selecione um condutor e tente novamente.";
        }

        if (customerId <= 0) {
            return "Cliente inválido.";
        }

        if (driverParameter.daysOff.some((day) => day < 0 || day > 6)) {
            return "Dia de folga inválido.";
        }

        if ((driverParameter.observation?.trim().length ?? 0) > 500) {
            return "A observação deve possuir no máximo 500 caracteres.";
        }

        if (newFiles.some((file) => (file.description?.trim().length ?? 0) > 250)) {
            return "A descrição do anexo deve possuir no máximo 250 caracteres.";
        }

        return null;
    }, [customerId, driverId, driverParameter, newFiles]);

    const handleSave = useCallback(async () => {
        const token: string = cookies.token || sessionStorage.getItem("token");
        const validationMessage = validate();

        if (validationMessage) {
            showMessage(validationMessage, "warn");
            return;
        }

        if (initialDriverParameter?.id != null && !hasChanges()) {
            showMessage("Nada foi alterado.", "warn");
            return;
        }

        const payload = buildDriverParameterPayload({
            driverParameter,
            driverId,
            customerId,
            newFiles,
            removedFiles,
        });

        setSaving(true);

        try {
            await saveDriverParameter(token, payload);
            await loadDriverParameter();
            showMessage(
                section === "attachments"
                    ? "Anexos do condutor salvos com sucesso."
                    : "Parâmetros do condutor salvos com sucesso.",
                "success",
            );
        } catch (error: any) {
            console.error("Erro ao salvar parâmetros do condutor:", error);
            showMessage(
                getApiErrorMessage(
                    error,
                    section === "attachments"
                        ? "Erro ao salvar os anexos do condutor."
                        : "Erro ao salvar os parâmetros do condutor.",
                ),
                error?.response?.data?.reason ? "warn" : "error",
            );
        } finally {
            setSaving(false);
        }
    }, [
        cookies.token,
        customerId,
        driverId,
        driverParameter,
        hasChanges,
        initialDriverParameter?.id,
        loadDriverParameter,
        newFiles,
        removedFiles,
        section,
        showMessage,
        validate,
    ]);

    const visibleExistingFiles = useMemo(
        () =>
            driverParameter.files?.filter(
                (file) =>
                    file.activated !== false &&
                    file.id != null &&
                    !removedFiles.includes(file.id),
            ) ?? [],
        [driverParameter.files, removedFiles],
    );
    const attachmentCount = visibleExistingFiles.length + newFiles.length;

    const handleFileSelection = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const selectedFile = event.target.files?.[0];

            if (!selectedFile || !selectedFile.type.startsWith("image/")) {
                showMessage("Selecione um arquivo de imagem válido.", "warn");
                event.target.value = "";
                return;
            }

            if (attachmentCount >= MAX_ATTACHMENTS) {
                showMessage(
                    `É permitido anexar no máximo ${MAX_ATTACHMENTS} imagens.`,
                    "warn",
                );
                event.target.value = "";
                return;
            }

            const reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onloadend = () => {
                if (typeof reader.result !== "string") {
                    showMessage("Não foi possível ler o anexo selecionado.", "error");
                    return;
                }

                setNewFiles((previous) => [
                    ...previous,
                    {
                        clientId: `${Date.now()}-${Math.random()}`,
                        file: reader.result as string,
                        description: "",
                        name: selectedFile.name,
                        type: selectedFile.type,
                        activated: true,
                    },
                ]);

                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
            };
            reader.onerror = () => {
                showMessage("Não foi possível ler o anexo selecionado.", "error");
                event.target.value = "";
            };
        },
        [attachmentCount, showMessage],
    );

    const handleFileDownload = useCallback((fileUrl: string, fileName: string) => {
        const link = document.createElement("a");
        link.href = fileUrl;

        let extension = "";
        if (fileUrl.startsWith("data:")) {
            const mime = fileUrl.split(";")[0].split(":")[1];
            if (mime?.includes("/")) {
                extension = `.${mime.split("/")[1]}`;
            }
        }

        link.download = fileName.includes(".")
            ? fileName
            : `${fileName || "anexo"}${extension || ".png"}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }, []);

    const handleFileFullscreen = useCallback((fileUrl: string, fileName: string) => {
        if (document.fullscreenElement) {
            void document.exitFullscreen();
            return;
        }

        const element = document.createElement("img");
        element.className = "driver-parameter-img-anexo";
        element.id = fileName;
        element.src = fileUrl;
        element.style.objectFit = "contain";
        element.style.width = "100%";
        element.style.height = "100%";
        document.body.appendChild(element);

        void element.requestFullscreen().catch((error) => {
            console.error("Erro ao abrir anexo em tela cheia:", error);
            if (document.body.contains(element)) {
                document.body.removeChild(element);
            }
        });

        element.addEventListener("fullscreenchange", () => {
            if (!document.fullscreenElement && document.body.contains(element)) {
                document.body.removeChild(element);
            }
        });
    }, []);

    if (loading) {
        return (
            <Paper
                className="tw-rounded-xl tw-bg-white tw-p-6"
                elevation={0}
                sx={{ background: "#fff", padding: 4, borderRadius: 3 }}
            >
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    justifyContent="center"
                    role="status"
                    aria-live="polite"
                >
                    <CircularProgress size={24} sx={{ color: "#00B700" }} />
                    <Typography sx={{ fontFamily: "Prometo" }}>
                        {section === "attachments"
                            ? "Carregando anexos do condutor..."
                            : "Carregando parâmetros do condutor..."}
                    </Typography>
                </Stack>
            </Paper>
        );
    }

    return (
        <>
            {section === "parameters" ? (
                <Paper
                    className="tw-bg-white tw-p-2 sm:tw-p-3"
                    elevation={0}
                    sx={{
                        background: "#fff",
                        padding: { xs: 1, sm: 1.5 },
                        width: "100%",
                        border: "none",
                        borderRadius: 0,
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            backgroundColor: "#ffff",
                            transition: "box-shadow 200ms ease, background-color 200ms ease",
                        },
                        "& .MuiOutlinedInput-root:hover": {
                            backgroundColor: "rgba(0, 108, 119, 0.02)",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused": {
                            boxShadow: "0 0 0 3px rgba(0, 108, 119, 0.10)",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#006C77",
                        },
                    }}
                >
                    <Box className="row tw-m-0" sx={{ margin: 0 }}>
                        {workSchedulesEnabled && <Box className="col-md-6" sx={{ margin: "10px 0" }}>
                            <Autocomplete
                                id="chip-label-select-work-schedule"
                                className="input-select-work-schedule"
                                style={{
                                    width: "100%",
                                    minHeight: "56px",
                                    height: "56px",
                                    backgroundColor: "#ffffff29",
                                    borderColor: "lightgray",
                                }}
                                options={workSchedulesList}
                                value={
                                    workSchedulesList.find(
                                        (option) => option.id === driverParameter.workScheduleId,
                                    ) ?? null
                                }
                                onChange={(_, option) =>
                                    setDriverParameter((previous) => ({
                                        ...previous,
                                        workScheduleId: option?.id ?? null,
                                        workScheduleName: option?.name ?? null,
                                    }))
                                }
                                getOptionLabel={(option) =>
                                    `${option.name} (${option.startTime?.slice(0, 5)} às ${option.endTime?.slice(0, 5)})`
                                }
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                noOptionsText={"Nenhuma escala de trabalho cadastrada"}
                                renderInput={(params) => (
                                    <FormControl className="form-group" style={{ display: "flex" }}>
                                        <InputLabel shrink style={{ color: "rgba(0, 0, 0, 0.6)", fontFamily: "Prometo" }}>
                                            Escala de trabalho
                                        </InputLabel>
                                        <TextField
                                            {...params}
                                            variant="outlined"
                                            placeholder="Selecione uma escala de trabalho (Opcional)"
                                            autoComplete="off"
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                notched: true,
                                                label: "Escala de trabalho",
                                                classes: { notchedOutline: "custom-notch" },
                                            }}
                                        />
                                    </FormControl>
                                )}
                                disabled={isDisabled}
                            />
                        </Box>}

                        <Box className="col-md-12" sx={{ margin: "10px 0" }}>
                            <FormControl component="fieldset" disabled={isDisabled}>
                                <FormLabel component="legend" sx={{ fontFamily: "Prometo" }}>
                                    Dias de folga
                                </FormLabel>
                                <FormGroup row>
                                    {WEEK_DAYS.map((day) => (
                                        <FormControlLabel
                                            key={day.value}
                                            label={day.label}
                                            control={
                                                <Checkbox
                                                    checked={driverParameter.daysOff.includes(day.value)}
                                                    onChange={(event) =>
                                                        setDriverParameter((previous) => ({
                                                            ...previous,
                                                            daysOff: event.target.checked
                                                                ? [...previous.daysOff, day.value].sort((a, b) => a - b)
                                                                : previous.daysOff.filter((value) => value !== day.value),
                                                        }))
                                                    }
                                                />
                                            }
                                        />
                                    ))}
                                </FormGroup>
                            </FormControl>
                        </Box>

                        <Box className="col-md-12" sx={{ margin: "4px 0 10px" }}>
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                flexWrap="wrap"
                                gap={1}
                                sx={{
                                    "& .MuiFormControlLabel-root": {
                                        minHeight: 44,
                                        margin: 0,
                                        padding: "4px 10px",
                                        borderRadius: 2,
                                        backgroundColor: "rgba(0, 108, 119, 0.04)",
                                    },
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <GreenSwitch
                                            checked={driverParameter.makesExternalTrips}
                                            onChange={(event) =>
                                                setDriverParameter((previous) => ({
                                                    ...previous,
                                                    makesExternalTrips: event.target.checked,
                                                }))
                                            }
                                            disabled={isDisabled}
                                        />
                                    }
                                    label="Faz viagens externas"
                                    sx={{
                                        marginLeft: 0,
                                        gap: 1,
                                        "& .MuiFormControlLabel-label": {
                                            fontFamily: "Prometo",
                                            color: "gray",
                                        },
                                    }}
                                />

                                <FormControlLabel
                                    control={
                                        <GreenSwitch
                                            checked={driverParameter.receivesAlerts}
                                            onChange={(event) =>
                                                setDriverParameter((previous) => ({
                                                    ...previous,
                                                    receivesAlerts: event.target.checked,
                                                }))
                                            }
                                            disabled={isDisabled}
                                        />
                                    }
                                    label="Receber alertas deste condutor"
                                    sx={{
                                        marginLeft: 0,
                                        gap: 1,
                                        "& .MuiFormControlLabel-label": {
                                            fontFamily: "Prometo",
                                            color: "gray",
                                        },
                                    }}
                                />
                            </Stack>
                        </Box>

                        <Box className="col-md-12" sx={{ margin: "10px 0" }}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label="Observação"
                                value={driverParameter.observation ?? ""}
                                placeholder="Digite a observação (Opcional)"
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <DescriptionOutlined />
                                        </InputAdornment>
                                    ),
                                }}
                                inputProps={{ maxLength: 500 }}
                                helperText={`${driverParameter.observation?.length ?? 0}/500`}
                                onChange={(event) =>
                                    setDriverParameter((previous) => ({
                                        ...previous,
                                        observation: event.target.value,
                                    }))
                                }
                                disabled={isDisabled}
                            />
                        </Box>
                    </Box>
                </Paper>
            ) : (
                <Paper
                    className="tw-bg-white tw-p-2 sm:tw-p-3"
                    elevation={0}
                    sx={{
                        padding: { xs: 1, sm: 1.5 },
                        background: "#fff",
                        border: "none",
                        borderRadius: 0,
                    }}
                >
                    <Typography
                        sx={{ fontFamily: "Prometo", fontWeight: "bold", marginBottom: 1 }}
                    >
                        Anexos vinculados diretamente ao condutor
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "Prometo", color: "gray", marginBottom: 2 }}
                    >
                        Estes anexos são independentes dos anexos dos veículos.
                    </Typography>

                    <input
                        type="file"
                        hidden
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleFileSelection}
                    />

                    <Box
                        className="attachment-dropzone tw-flex tw-min-h-[220px] tw-flex-wrap tw-content-start tw-items-start tw-justify-start tw-gap-2 tw-rounded-xl tw-border tw-border-dashed tw-border-black/15 tw-bg-slate-50/70 tw-p-4"
                        sx={{
                            minHeight: 220,
                            border: "1px dashed rgba(0, 63, 74, 0.20)",
                            borderRadius: 3,
                            backgroundColor: "rgba(248, 250, 252, 0.70)",
                            padding: 2,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            gap: 1,
                        }}
                    >
                        {visibleExistingFiles.map((file, index) => (
                            <AttachmentCard
                                key={file.id}
                                attachment={file}
                                onDownload={() =>
                                    handleFileDownload(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onFullscreen={() =>
                                    handleFileFullscreen(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onRemove={
                                    !isDisabled && file.id != null
                                        ? () =>
                                            setRemovedFiles((previous) =>
                                                previous.includes(file.id as number)
                                                    ? previous
                                                    : [...previous, file.id as number],
                                            )
                                        : undefined
                                }
                            />
                        ))}

                        {newFiles.map((file, index) => (
                            <AttachmentCard
                                key={file.clientId}
                                attachment={file}
                                editableDescription={!isDisabled}
                                onDescriptionChange={(description) =>
                                    setNewFiles((previous) =>
                                        previous.map((item) =>
                                            item.clientId === file.clientId
                                                ? { ...item, description }
                                                : item,
                                        ),
                                    )
                                }
                                onDownload={() =>
                                    handleFileDownload(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onFullscreen={() =>
                                    handleFileFullscreen(
                                        file.file,
                                        file.name || `anexo-${index + 1}`,
                                    )
                                }
                                onRemove={
                                    !isDisabled
                                        ? () =>
                                            setNewFiles((previous) =>
                                                previous.filter(
                                                    (item) =>
                                                        item.clientId !== file.clientId,
                                                ),
                                            )
                                        : undefined
                                }
                            />
                        ))}

                        {attachmentCount === 0 && isDisabled && (
                            <Stack alignItems="center" spacing={1}>
                                <HideImageOutlined sx={{ fontSize: 44, color: "#6c757d" }} />
                                <Typography sx={{ fontFamily: "Prometo", color: "gray" }}>
                                    Nenhum anexo encontrado para este condutor.
                                </Typography>
                            </Stack>
                        )}

                        {!isDisabled && attachmentCount < MAX_ATTACHMENTS && (
                            <Box
                                sx={{
                                    minHeight: attachmentCount > 0 ? 170 : 45,
                                    display: "flex",
                                    alignItems:
                                        attachmentCount > 0 ? "center" : "flex-start",
                                }}
                            >
                                <Chip
                                    label="Anexar"
                                    sx={{
                                        color: "#00B700",
                                        "& .MuiChip-icon": { marginLeft: 0 },
                                    }}
                                    icon={
                                        <Fab
                                            size="small"
                                            aria-label="Adicionar anexo do condutor"
                                            onClick={(event) => {
                                                event.preventDefault();
                                                fileInputRef.current?.click();
                                            }}
                                            sx={{
                                                marginRight: 1,
                                                background: "#00B700",
                                                color: "#fff",
                                                "&:hover": { background: "#009900" },
                                            }}
                                        >
                                            <AddOutlined />
                                        </Fab>
                                    }
                                />
                            </Box>
                        )}
                    </Box>

                    <Typography
                        variant="caption"
                        sx={{ display: "block", marginTop: 1, color: "gray", fontFamily: "Prometo" }}
                    >
                        Máximo de {MAX_ATTACHMENTS} anexos de imagem. A remoção é efetivada ao
                        salvar.
                    </Typography>
                </Paper>
            )}

            <Box
                className="tw-mt-4 tw-border-t tw-border-black/10 tw-pt-4"
                sx={{ paddingTop: 2, marginTop: 2, borderTop: "1px solid rgba(0, 0, 0, 0.10)" }}
            >
                <Paper
                    className="tw-flex tw-w-full tw-flex-col-reverse tw-gap-2 sm:tw-flex-row sm:tw-justify-end"
                    elevation={0}
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column-reverse", sm: "row" },
                        justifyContent: "flex-end",
                        width: { xs: "100%", sm: "auto" },
                        marginLeft: "auto",
                        alignSelf: "flex-end",
                        gap: 1,
                        background: "transparent",
                    }}
                >
                    <Button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        className="btn btn-primary btn-block login-button-close tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                        sx={{ fontFamily: "Prometo", color: "#fff", marginRight: { xs: 0, sm: 1 }, width: { xs: "100%", sm: 130 } }}
                    >
                        <CloseOutlined sx={{ marginRight: 1 }} />
                        Fechar
                    </Button>

                    <Button
                        type="button"
                        onClick={handleSave}
                        disabled={isDisabled || saving}
                        className="btn btn-primary btn-block login-button tw-transition-transform tw-duration-200 hover:tw-translate-y-[-1px]"
                        sx={{ fontFamily: "Prometo", color: "#fff", width: { xs: "100%", sm: 130 } }}
                    >
                        {saving ? (
                            <CircularProgress size={20} sx={{ color: "#fff", marginRight: 1 }} />
                        ) : (
                            <SaveOutlined style={{ color: "#fff", marginRight: 5 }} />
                        )}

                        <Typography
                            className="forgot-password"
                            style={{
                                fontFamily: "Prometo",
                                fontSize: "16px",
                                color: "#ffff",
                            }}
                        >
                            {saving ? "Salvando..." : "Salvar"}
                        </Typography>
                    </Button>
                </Paper>
            </Box>
        </>
    );
};

export default DriverParameter;
